import { Box, Typography, useMediaQuery, Stack, useTheme } from "@mui/material";
import {
  ManualPick20Px,
  R5Robot20Px,
  autostoreTheme,
  formatDateDayjs,
  useToast,
  ASTableV2,
  ASTableV2Body,
  ASTableV2Cell,
  ASTableV2Header,
  ASTableV2Row,
  ASTableV2RowSkeleton,
  ASTableV2RowEmpty
} from "@qubit/autoparts";
import { t } from "i18next";
import { useEffect } from "react";

import { useAppDispatch, useAppSelector } from "~/app/store";

import { TemperatureZoneBadgeCell } from "~/components/TemperatureZoneBadgeCell";
import { useGetMultiTempGridInfo } from "~/hooks/useGetMultiTempGridInfo";
import {
  inventoryDateLabel,
  generateLocationNameFromInventorySummary,
  checkIsExpiration,
  HoldTypeSource
} from "~/lib/helpers";
import { mixpanelTrack, useMixpanelPageName } from "~/lib/mixpanel-tracking";
import { GetPortResponse } from "~/redux/actions";
import { clearSelectedVariant } from "~/redux/actions/inventory";
import { selectServerConfig } from "~/redux/selectors/appSelectors";
import { selectIsAdjustingBins } from "~/redux/selectors/inventorySelectors";
import { useLazyGetBinQuery } from "~/redux/warehouse/autostoreGrid.hooks";
import { InventorySummaryDto, HoldDto } from "~/types/api";

import { InventoryBackButton } from "./InventoryBackButton";
import { InventoryNavbarSearch } from "./InventoryNavbarSearch";
import {
  setAllInventoryPage,
  setAdjustingInventoryPage,
  setSelectedSummaries,
  setSelectedAdjustingSummary,
  appendSummary
} from "./inventory.slice";
import { useViewType } from "./useViewType";

interface InventoryTablesProps {
  summariesToDisplay: InventorySummaryDto[];
  portStateByPortArray: {
    getPortResponse: GetPortResponse;
    timestamp: Date;
  }[];
  selectedAutostoreGridId?: Guid;
  isGetBinsLoading: boolean;
  handleFetchAllInventory: () => void;
  hasInventoryTableV2EnhancedFlag?: boolean;
}

export const limit = 5;

export const InventoryTables = (props: InventoryTablesProps) => {
  const {
    summariesToDisplay,
    portStateByPortArray,
    selectedAutostoreGridId,
    isGetBinsLoading,
    handleFetchAllInventory,
    hasInventoryTableV2EnhancedFlag = false
  } = props;
  const dispatch = useAppDispatch();
  const { palette } = useTheme();
  const isMobile = useMediaQuery(autostoreTheme.breakpoints.down("sm"));
  const trackedPageName = useMixpanelPageName();
  const { isMultiTempGrid } = useGetMultiTempGridInfo();

  const { isBinView, isProductView, isProductsView, isHoldsPage } =
    useViewType();
  const isProductOrBinView = isBinView || isProductView;
  const isAdjustingBins = useAppSelector(selectIsAdjustingBins);
  const loadingInventorySummaries = useAppSelector(
    (state) => state.inventory.loadingInventorySummaries
  );

  const { inv_inventoryDateLabel } = useAppSelector(selectServerConfig);

  const inventorySummaries = useAppSelector(
    (state) => state.inventory.inventorySummaries
  );
  const portStateBinIds = portStateByPortArray.map(
    (port) => port.getPortResponse.selectedBin
  );
  const inventorySummariesCount = useAppSelector(
    (state) => state.inventory.inventorySummariesCount
  );
  const allInventoryPage = useAppSelector(
    (state) => state.inventoryNew.allInventoryPage
  );
  const adjustingInventoryPage = useAppSelector(
    (state) => state.inventoryNew.adjustingInventoryPage
  );
  const selectedAdjustingSummary = useAppSelector(
    (state) => state.inventoryNew.selectedAdjustingSummary
  );
  const selectedSummaries = useAppSelector(
    (state) => state.inventoryNew.selectedSummaries
  );
  const workstationPortIds = useAppSelector(
    (state) => state.workstations.siteAllPortIds
  );

  const selectedSummariesAtPort = selectedSummaries
    .filter((sum) => portStateBinIds.includes(sum.autostoreBinNumber || 0))
    .map((sum) => sum.inventoryId);
  const selectedRows = selectedSummaries.map((sum) => sum.inventoryId);

  const { warningToast, errorToast } = useToast();

  // RTKQ
  const [fetchBinInfo] = useLazyGetBinQuery();
  // if there's only one inventory line at workstation, select it
  useEffect(() => {
    if (
      isAdjustingBins &&
      selectedSummariesAtPort.length === 1 &&
      !selectedAdjustingSummary
    ) {
      const summaryToSelect = selectedSummaries.find(
        (s) => s.inventoryId === selectedSummariesAtPort[0]
      );
      if (summaryToSelect)
        dispatch(setSelectedAdjustingSummary(summaryToSelect));
    }
  }, [
    dispatch,
    isAdjustingBins,
    selectedSummaries,
    selectedSummariesAtPort,
    selectedAdjustingSummary
  ]);

  const fetchBinInfoCallback = (binNumber: number): Promise<boolean> => {
    if (!selectedAutostoreGridId) return Promise.resolve(false);

    return fetchBinInfo({
      autostoreGridId: selectedAutostoreGridId,
      binNumber: binNumber
    })
      .unwrap()
      .then((binStatus) => {
        const { binMode, contentCode } = binStatus.binState;
        const flags = binStatus.flags;
        if (
          binMode === "O" &&
          !workstationPortIds.includes(binStatus.binState.portId)
        ) {
          warningToast(t("bin is at different port", { binNumber }));
          return true;
        }

        if (binMode === "X") {
          warningToast(t("bin is outside the grid", { binNumber }));
          return true;
        }

        if (contentCode === 0 || flags?.includes("bin locked")) {
          warningToast(t("bin is locked", { binNumber }));
          return true;
        }

        return false;
      })
      .catch(() => {
        errorToast(`Failed to query bin ${binNumber}`);
        return false; // Resolve the promise with false in case of error
      });
  };

  const onBinRowClick = async (row: InventorySummaryDto) => {
    const rowId = row.inventoryId;
    if (selectedRows.includes(rowId)) {
      dispatch(
        setSelectedSummaries(
          selectedSummaries.filter((summary) => summary.inventoryId !== rowId)
        )
      );
      if (selectedRows.length === 1 && (isBinView || isProductsView)) {
        dispatch(clearSelectedVariant());
      }
      mixpanelTrack({
        trackedPageName,
        type: "Unselect Row",
        label: "Unselect Inventory Row",
        eventProperties: row
      });
    } else {
      const selectedInventorySummary = inventorySummaries.find(
        (summary) => summary.inventoryId === rowId
      );
      mixpanelTrack({
        trackedPageName,
        type: "Select Row",
        label: "Select Inventory Row",
        eventProperties: row
      });
      if (selectedInventorySummary?.autostoreBinNumber) {
        // if select inventory bin is at another port, outside of grid or locked, don't add the inventory to the selectedRows.
        const isBinUngettable = await fetchBinInfoCallback(
          selectedInventorySummary.autostoreBinNumber
        );
        if (isBinUngettable) return;
      }
      dispatch(appendSummary(row));
    }
  };

  const summariesPagination: Parameters<typeof ASTableV2>[0]["pagination"] = {
    page: allInventoryPage,
    pageCount: inventorySummariesCount
      ? Math.ceil(inventorySummariesCount / limit)
      : 0,
    setPage: (p) => {
      dispatch(setAllInventoryPage(p));
    }
  };

  const adjustingBinsPagination: Parameters<typeof ASTableV2>[0]["pagination"] =
    {
      page: adjustingInventoryPage,
      pageCount: Math.ceil(selectedSummaries.length / limit),
      setPage: (p) => {
        dispatch(setAdjustingInventoryPage(p));
      }
    };

  // if the user somehow ends up on a table page with no rows, go back a page
  useEffect(() => {
    if (
      adjustingBinsPagination.pageCount &&
      adjustingInventoryPage > adjustingBinsPagination.pageCount
    ) {
      dispatch(setAdjustingInventoryPage(adjustingInventoryPage - 1));
    }
  }, [adjustingInventoryPage, dispatch, adjustingBinsPagination.pageCount]);

  const isLoading = !isAdjustingBins && loadingInventorySummaries;
  const rowData = isAdjustingBins ? summariesToDisplay : inventorySummaries;

  const contentColumnsLayout = `${isAdjustingBins ? "" : "1fr"} repeat(${isMobile ? 4 : 5}, auto)`;
  const gridTemplateColumns = isMultiTempGrid
    ? `28px ${contentColumnsLayout}`
    : contentColumnsLayout;
  const baseColumns = isAdjustingBins ? 5 : 6;
  const skeletonColumnsCount = baseColumns + (isMultiTempGrid ? 1 : 0);

  return (
    <Stack sx={{ height: "100%", minHeight: 0, overflow: "hidden" }}>
      <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, overflow: "hidden" }}>
        <ASTableV2
          gridTemplateColumns={gridTemplateColumns}
          gridTemplateRows="50px repeat(5, 1fr)"
          isLoading={false}
          containerSx={{ height: "100%", minHeight: 0 }}
          pagination={
            isAdjustingBins ? adjustingBinsPagination : summariesPagination
          }
          searchComponent={
            hasInventoryTableV2EnhancedFlag &&
            !isAdjustingBins &&
            !isProductOrBinView ? (
              <InventoryNavbarSearch />
            ) : undefined
          }
          toolbarStart={
            isAdjustingBins || isProductOrBinView ? (
              <InventoryBackButton
                isAdjustingBins={isAdjustingBins}
                isGetBinsLoading={isGetBinsLoading}
                handleFetchAllInventory={handleFetchAllInventory}
              />
            ) : undefined
          }
        >
          <ASTableV2Header>
            <ASTableV2Row>
              {isMultiTempGrid && <ASTableV2Cell />}
              {!isAdjustingBins && (
                <ASTableV2Cell>
                  <Typography variant="tableBody">{t("product")}</Typography>
                </ASTableV2Cell>
              )}
              <ASTableV2Cell>
                <Typography variant="tableBody">{t("bin")}</Typography>
              </ASTableV2Cell>
              <ASTableV2Cell>
                <Typography variant="tableBody">{t("quantity")}</Typography>
              </ASTableV2Cell>
              {!isMobile && (
                <ASTableV2Cell>
                  <Typography variant="tableBody">{t("hold type")}</Typography>
                </ASTableV2Cell>
              )}
              <ASTableV2Cell>
                <Typography variant="tableBody">{t("lot number")}</Typography>
              </ASTableV2Cell>
              <ASTableV2Cell>
                <Typography variant="tableBody">
                  {t(inventoryDateLabel(inv_inventoryDateLabel))}
                </Typography>
              </ASTableV2Cell>
            </ASTableV2Row>
          </ASTableV2Header>
          <ASTableV2Body>
            {isLoading && (
              <ASTableV2RowSkeleton
                rows={limit}
                columns={skeletonColumnsCount}
              />
            )}
            {!isLoading && !rowData.length && (
              <ASTableV2RowEmpty>
                {isHoldsPage
                  ? t("no inventory holds found")
                  : t("no inventory found")}
              </ASTableV2RowEmpty>
            )}
            {!isLoading &&
              rowData.map((row) => (
                <ASTableV2Row
                  key={row.inventoryId}
                  selected={
                    isAdjustingBins
                      ? selectedAdjustingSummary?.inventoryId ===
                        row.inventoryId
                      : selectedRows.includes(row.inventoryId)
                  }
                  highlighted={
                    isAdjustingBins &&
                    selectedSummariesAtPort.includes(row.inventoryId)
                  }
                  onClick={() => {
                    if (isAdjustingBins) {
                      dispatch(setSelectedAdjustingSummary(row));
                      mixpanelTrack({
                        trackedPageName,
                        type: "Select Row",
                        label: "Select Inventory Row To Adjust",
                        eventProperties: {
                          "Inventory ID": row.inventoryId,
                          "Product Name": row.productName,
                          "Autostore Bin Number": row.autostoreBinNumber
                        }
                      });
                      return;
                    } else {
                      return onBinRowClick(row);
                    }
                  }}
                >
                  <TemperatureZoneBadgeCell tempZone={row.temperatureZone} />
                  {!isAdjustingBins && (
                    <ASTableV2Cell>
                      <div>
                        <Typography variant="tableBody">
                          {row.productName}
                        </Typography>
                        <Stack
                          sx={{ marginTop: 1, textTransform: "capitalize" }}
                          direction="row"
                          spacing={1}
                        >
                          {row.binType === "autostore" ? (
                            <R5Robot20Px
                              style={{ fill: palette.text.secondary }}
                            />
                          ) : (
                            <ManualPick20Px
                              style={{ fill: palette.text.secondary }}
                            />
                          )}
                          <Typography variant="caption" color="text.secondary">
                            {row.binType === "autostore"
                              ? t("autostore")
                              : row.binType}
                          </Typography>
                        </Stack>
                      </div>
                    </ASTableV2Cell>
                  )}
                  <ASTableV2Cell>
                    <Stack direction="column" spacing={1}>
                      <Typography variant="tableBody">
                        {row.binType === "autostore"
                          ? `${row.autostoreBinNumber || ""}`
                          : generateLocationNameFromInventorySummary(row)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {`${t("compartment")}: ${row.autostoreCompartmentNumber}`}
                      </Typography>
                    </Stack>
                  </ASTableV2Cell>
                  <ASTableV2Cell>
                    <div>
                      <Typography variant="tableBody">
                        {`${row.count?.value || t("n/a")} ${row.count?.units || ""}`}
                      </Typography>
                    </div>
                  </ASTableV2Cell>
                  {!isMobile && (
                    <ASTableV2Cell>
                      <Box>
                        {row.holds?.map((hold: HoldDto) => {
                          const sourceText = t(
                            hold.source.toLowerCase() as HoldTypeSource
                          );
                          return (
                            <Box key={`${row.inventoryId}-${hold.reasonCode}`}>
                              <Typography variant="tableBody">{`${t(
                                hold.reasonCode as HoldTypeSource
                              )} (${sourceText})`}</Typography>
                            </Box>
                          );
                        }) || ""}
                      </Box>
                    </ASTableV2Cell>
                  )}
                  <ASTableV2Cell>
                    {row.lotNumber && (
                      <Typography variant="tableBody">
                        {row.lotNumber}
                      </Typography>
                    )}
                  </ASTableV2Cell>
                  <ASTableV2Cell>
                    <Typography variant="tableBody">
                      {formatDateDayjs(
                        checkIsExpiration(inv_inventoryDateLabel)
                          ? row.expiration
                          : row.manufactureDate
                      )}
                    </Typography>
                  </ASTableV2Cell>
                </ASTableV2Row>
              ))}
          </ASTableV2Body>
        </ASTableV2>
      </Box>
    </Stack>
  );
};
